<template>
    <div v-show="stepActive" class="tournament-preset">
        <h2 v-if="!hideMeta" class="title">{{ $t('title_TournamentPreset') }}</h2>
        <p v-if="!hideMeta" class="subtitle">{{ $t('subtitle_TournamentPreset') }}</p>

        <div class="info-card">
            <div v-if="loading" class="preset-state">
                <v-progress-circular indeterminate size="28" width="3" color="primary" />
                <span>{{ $t('preset_TournamentPreset.loading') }}</span>
            </div>

            <template v-else>
                <!-- Presets are served by a broker-staff endpoint (TD-022): a club
                     admin without staff access gets 403 and can still start blank. -->
                <div v-if="loadError" class="preset-notice" :class="{ 'preset-notice--error': !forbidden }">
                    <i class="fa-regular fa-circle-info mr-2" aria-hidden="true"></i>
                    <span>{{ $t(forbidden ? 'preset_TournamentPreset.unavailable' : 'preset_TournamentPreset.loadError') }}</span>
                    <button v-if="!forbidden" type="button" class="preset-notice__retry" @click="loadPresets">
                        {{ $t('preset_TournamentPreset.retry') }}
                    </button>
                </div>

                <div class="preset-grid">
                    <button
                        v-for="preset in presets"
                        :key="preset.key"
                        type="button"
                        class="preset-card"
                        :class="{ 'preset-card--selected': isSelected(preset.key) }"
                        :aria-pressed="isSelected(preset.key) ? 'true' : 'false'"
                        @click="choosePreset(preset)"
                    >
                        <span v-if="isSelected(preset.key)" class="preset-card__check" :aria-label="$t('preset_TournamentPreset.selected')">
                            <i class="fa-regular fa-check" aria-hidden="true"></i>
                        </span>
                        <span class="preset-card__icon">
                            <i :class="presetIcon(preset)" aria-hidden="true"></i>
                        </span>
                        <span class="preset-card__name">{{ preset.name }}</span>
                        <span v-if="preset.description" class="preset-card__tagline">{{ preset.description }}</span>
                        <span class="preset-card__chips">
                            <span v-for="(chip, i) in presetChips(preset)" :key="i" class="preset-chip">{{ chip }}</span>
                        </span>
                        <ul v-if="presetBullets(preset).length" class="preset-card__rules">
                            <li v-for="(bullet, i) in presetBullets(preset)" :key="i">
                                <i class="fa-regular fa-circle-check" aria-hidden="true"></i>
                                <span>{{ bullet }}</span>
                            </li>
                        </ul>
                    </button>

                    <button
                        type="button"
                        class="preset-card preset-card--blank"
                        :class="{ 'preset-card--selected': isSelected(BLANK_PRESET_KEY) }"
                        :aria-pressed="isSelected(BLANK_PRESET_KEY) ? 'true' : 'false'"
                        @click="chooseBlank"
                    >
                        <span v-if="isSelected(BLANK_PRESET_KEY)" class="preset-card__check" :aria-label="$t('preset_TournamentPreset.selected')">
                            <i class="fa-regular fa-check" aria-hidden="true"></i>
                        </span>
                        <span class="preset-card__icon preset-card__icon--blank">
                            <i class="fa-regular fa-plus" aria-hidden="true"></i>
                        </span>
                        <span class="preset-card__name">{{ $t('preset_TournamentPreset.blankTitle') }}</span>
                        <span class="preset-card__tagline">{{ $t('preset_TournamentPreset.blankDescription') }}</span>
                    </button>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import {
    BLANK_PRESET_KEY,
    applyBlankToForm,
    applyPresetToForm,
    fetchCompetitionPresets,
    presetIcon
} from '@/services/competitionPresets';
import { PERCENTAGE_RULE_TYPES, RULE_LABEL_KEYS, TOGGLE_RULE_TYPES } from '../tournamentRuleUtils';

const MILESTONE_METRIC_KEYS = {
    TOTAL_CLOSED_LOTS: 'preset_TournamentPreset.metrics.TOTAL_CLOSED_LOTS'
};

/**
 * Step 1 of the competition wizard (SFB-1289): pick a preset or start blank.
 * Selecting a card applies the preset defaults to the shared tournamentForm;
 * the following steps keep their own validations and remain editable.
 */
export default {
    name: 'TournamentPreset',
    props: {
        stepActive: { type: Boolean, default: false },
        setNextBtnDisabled: { type: Function, required: true },
        hideMeta: { type: Boolean, default: false }
    },
    data() {
        return {
            BLANK_PRESET_KEY,
            presets: [],
            loading: false,
            loadError: false,
            forbidden: false
        };
    },
    computed: {
        form() {
            return this.$store.state.tournamentForm;
        },
        hasSelection() {
            return !!this.form.presetSelected;
        }
    },
    watch: {
        stepActive: {
            immediate: true,
            handler(active) {
                if (active) this.setNextBtnDisabled(!this.hasSelection);
            }
        },
        hasSelection(value) {
            if (this.stepActive) this.setNextBtnDisabled(!value);
        }
    },
    created() {
        this.loadPresets();
    },
    methods: {
        presetIcon,
        isSelected(key) {
            return this.form.presetSelected && this.form.presetKey === key;
        },
        choosePreset(preset) {
            if (this.isSelected(preset.key)) return;
            applyPresetToForm(this.$store, preset);
        },
        chooseBlank() {
            if (this.isSelected(BLANK_PRESET_KEY)) return;
            applyBlankToForm(this.$store);
        },
        async loadPresets() {
            this.loading = true;
            this.loadError = false;
            this.forbidden = false;
            try {
                this.presets = await fetchCompetitionPresets();
            } catch (error) {
                this.presets = [];
                this.loadError = true;
                this.forbidden = [401, 403].includes(error?.response?.status);
                // eslint-disable-next-line no-console
                console.error('[wizard] loading competition presets failed', error);
            } finally {
                this.loading = false;
            }
        },
        presetChips(preset) {
            const chips = [];
            if (preset.accountType) {
                chips.push(this.$t(`tournamentType_TournamentDetails.${preset.accountType.toLowerCase()}.label`));
            }
            if (preset.durationDays > 0) {
                chips.push(this.$t('preset_TournamentPreset.days', { count: preset.durationDays }));
            }
            if (preset.rankingType) {
                chips.push(this.rankingLabel(preset.rankingType));
            }
            if (preset.initialBalance != null) {
                chips.push(this.$t('preset_TournamentPreset.initialBalance', { amount: this.formatMoney(preset.initialBalance) }));
            } else if (preset.minimumBalance != null) {
                chips.push(this.$t('preset_TournamentPreset.minimumBalance', { amount: this.formatMoney(preset.minimumBalance) }));
            }
            return chips;
        },
        presetBullets(preset) {
            const bullets = (preset.rules || []).map((rule) => this.ruleText(rule));
            (preset.milestones || []).forEach((milestone) => {
                bullets.push(this.$t('preset_TournamentPreset.milestone', {
                    points: milestone.pointsPerTier != null ? milestone.pointsPerTier : 1,
                    tier: this.formatNumber(milestone.tier),
                    metric: this.milestoneMetricLabel(milestone.metric)
                }));
            });
            return bullets;
        },
        ruleText(rule) {
            const label = this.ruleLabel(rule.ruleType);
            if (TOGGLE_RULE_TYPES.includes(rule.ruleType) || rule.value == null) return label;
            const value = PERCENTAGE_RULE_TYPES.includes(rule.ruleType)
                ? `${this.formatNumber(rule.value * 100)}%`
                : this.formatNumber(rule.value);
            return `${label}: ${value}`;
        },
        ruleLabel(type) {
            const key = RULE_LABEL_KEYS[type];
            return key ? this.$t(key) : this.humanize(type);
        },
        rankingLabel(code) {
            const key = `preset_TournamentPreset.ranking.${code}`;
            const translated = this.$t(key);
            return translated !== key ? translated : this.humanize(code);
        },
        milestoneMetricLabel(metric) {
            const key = MILESTONE_METRIC_KEYS[metric];
            return key ? this.$t(key) : this.humanize(metric);
        },
        humanize(code) {
            return String(code || '').toLowerCase().replace(/_/g, ' ');
        },
        formatNumber(value) {
            const number = Number(value);
            if (Number.isNaN(number)) return String(value ?? '');
            return number.toLocaleString(undefined, { maximumFractionDigits: 2 });
        },
        formatMoney(value) {
            return `$${this.formatNumber(value)}`;
        }
    }
};
</script>

<style lang="scss" scoped>
.tournament-preset {
    width: 100%;

    .title {
        font-size: 32px !important;
        font-weight: bold;
        margin-bottom: 8px;
        font-family: "Avenir", sans-serif;
        color: var(--v-textPrimary-base);
    }

    .subtitle {
        font-size: 16px;
        color: var(--v-textSecondary-base);
        margin-bottom: 24px;
        font-family: "Avenir", sans-serif;
    }

    .info-card {
        background-color: var(--v-cardBackground-base);
        border: 1px solid #9A9A9A;
        border-radius: 10px;
        padding: 40px;
    }

    .preset-state {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        min-height: 160px;
        color: var(--v-textSecondary-base);
        font-size: 14px;
    }

    .preset-notice {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 4px;
        margin-bottom: 20px;
        padding: 12px 16px;
        border-radius: 8px;
        font-size: 14px;
        color: var(--v-textSecondary-base);
        background: rgba(154, 154, 154, 0.12);

        &--error {
            color: #C03131;
            background: rgba(192, 49, 49, 0.08);
        }

        &__retry {
            margin-left: auto;
            background: none;
            border: none;
            padding: 0;
            font: inherit;
            font-weight: 700;
            color: inherit;
            text-decoration: underline;
            cursor: pointer;
        }
    }

    .preset-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
        gap: 20px;
    }

    // Cards are <button>s so they're keyboard-selectable; reset button chrome.
    .preset-card {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
        width: 100%;
        min-height: 100%;
        padding: 24px;
        text-align: left;
        font: inherit;
        color: var(--v-textPrimary-base);
        background: var(--v-cardBackground-base);
        border: 2px solid rgba(154, 154, 154, 0.5);
        border-radius: 14px;
        cursor: pointer;
        transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;

        &:hover,
        &:focus-visible {
            border-color: var(--v-primary-base);
            outline: none;
        }

        &--selected {
            border-color: var(--v-primary-base);
            background: rgba(40, 234, 150, 0.08);
            box-shadow: 0 0 0 3px rgba(40, 234, 150, 0.18);
        }

        &--blank {
            border-style: dashed;
            justify-content: flex-start;
        }

        &__check {
            position: absolute;
            top: 14px;
            right: 14px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            background: var(--v-primary-base);
            color: #0B1F17;
            font-size: 12px;
        }

        &__icon {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 44px;
            height: 44px;
            border-radius: 12px;
            background: rgba(40, 234, 150, 0.15);
            color: #0F8F5A;
            font-size: 20px;

            &--blank {
                background: rgba(154, 154, 154, 0.15);
                color: var(--v-textSecondary-base);
            }
        }

        &__name {
            font-size: 20px;
            font-weight: 700;
            line-height: 1.2;
            font-family: "Avenir", sans-serif;
        }

        &__tagline {
            font-size: 14px;
            line-height: 1.45;
            color: var(--v-textSecondary-base);
        }

        &__chips {
            display: flex;
            flex-wrap: wrap;
            gap: 6px;
        }

        &__rules {
            list-style: none;
            padding: 0;
            margin: 4px 0 0;
            display: flex;
            flex-direction: column;
            gap: 6px;
            font-size: 13px;
            line-height: 1.4;

            li {
                display: flex;
                align-items: flex-start;
                gap: 8px;
            }

            i {
                color: var(--v-mainGreen-base);
                margin-top: 2px;
                flex: 0 0 auto;
            }
        }
    }

    .preset-chip {
        display: inline-block;
        padding: 3px 10px;
        border-radius: 999px;
        font-size: 12px;
        font-weight: 600;
        color: var(--v-textPrimary-base);
        background: rgba(154, 154, 154, 0.18);
        white-space: nowrap;
    }

    @media (max-width: 960px) {
        .title { font-size: 22px !important; }
        .subtitle { font-size: 14px; }

        .info-card {
            padding: 24px 16px;
        }

        .preset-grid {
            grid-template-columns: 1fr;
            gap: 14px;
        }

        .preset-card {
            padding: 18px;

            &__name { font-size: 18px; }
        }
    }
}

.theme--dark .tournament-preset {
    .preset-card {
        border-color: rgba(255, 255, 255, 0.16);

        &--selected {
            border-color: var(--v-primary-base);
            background: rgba(40, 234, 150, 0.1);
        }

        &__icon {
            color: var(--v-primary-base);
        }
    }

    .preset-chip {
        background: rgba(255, 255, 255, 0.1);
    }
}
</style>
